import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);

// Promo constants
const RETA_SLUG = 'retatrutide';
const GHKCU_SLUG = 'ghk-cu';
const GHKCU_50_LABEL = '50 mg';
const PAIR_DISCOUNT_PCT = 0.25;   // 25% off ONE GHK-Cu 50 mg vial when Reta is in the cart
const BULK_THRESHOLD = 10;        // 10+ of the same slug+variant
const BULK_DISCOUNT_PCT = 0.20;   // 20% off the whole line

// Compute discounts for the given cart items.
// Returns { subtotal, discountTotal, discounts, total }.
// Non-stacking rule: if a GHK-Cu 50 mg line qualifies for bulk, the pair discount does NOT apply on top.
export const computeDiscounts = (items) => {
  const subtotal = items.reduce((s, i) => s + (i.price || 0) * i.qty, 0);
  const discounts = [];

  // 1) Per-line bulk discount (qty >= 10 of same slug+variant).
  const bulkedKeys = new Set();
  items.forEach(i => {
    if (!i.price || i.qty < BULK_THRESHOLD) return;
    const lineAmt = i.price * i.qty;
    const amount = +(lineAmt * BULK_DISCOUNT_PCT).toFixed(2);
    discounts.push({
      code: 'BULK10',
      description: `Buy 10 & Save 20% — ${i.name}${i.variantLabel ? ' ' + i.variantLabel : ''}`,
      amount,
      slug: i.slug,
      variantLabel: i.variantLabel || null,
    });
    bulkedKeys.add(i.key);
  });

  // 2) Reta + GHK-Cu 50 mg pair discount (only if a GHK-Cu 50 mg line exists AND is not bulked).
  const hasReta = items.some(i => i.slug === RETA_SLUG && i.qty > 0);
  const ghkcu50 = items.find(i => i.slug === GHKCU_SLUG && i.variantLabel === GHKCU_50_LABEL && i.qty > 0);
  if (hasReta && ghkcu50 && !bulkedKeys.has(ghkcu50.key)) {
    const amount = +((ghkcu50.price || 0) * PAIR_DISCOUNT_PCT).toFixed(2);
    if (amount > 0) {
      discounts.push({
        code: 'RETA_GHKCU',
        description: 'Reta + GHK-Cu 50 mg — 25% off (1 vial)',
        amount,
        slug: ghkcu50.slug,
        variantLabel: ghkcu50.variantLabel,
      });
    }
  }

  const discountTotal = +discounts.reduce((s, d) => s + d.amount, 0).toFixed(2);
  const total = Math.max(0, +(subtotal - discountTotal).toFixed(2));
  return { subtotal, discountTotal, discounts, total };
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem('vrr_cart');
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('vrr_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1, variant = null, opts = {}) => {
    setItems(prev => {
      const key = variant ? `${product.slug}-${variant.label}` : product.slug;
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? { ...i, qty: i.qty + qty, preorder: i.preorder || !!opts.preorder } : i);
      }
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          name: product.name,
          image: product.image,
          dose: variant ? variant.label : product.dose,
          variantLabel: variant ? variant.label : null,
          price: variant ? variant.price : product.price,
          priceDisplay: variant ? variant.priceDisplay : product.priceDisplay,
          preorder: !!opts.preorder,
          qty
        }
      ];
    });
    setOpen(true);
  };

  const removeItem = (key) => setItems(prev => prev.filter(i => i.key !== key));
  const updateQty = (key, qty) => setItems(prev => prev.map(i => i.key === key ? { ...i, qty: Math.max(1, qty) } : i));
  const clear = () => setItems([]);

  const { subtotal, discountTotal, discounts, total } = useMemo(() => computeDiscounts(items), [items]);
  const count = items.reduce((sum, i) => sum + i.qty, 0);

  const value = { items, addItem, removeItem, updateQty, clear, subtotal, discountTotal, discounts, total, count, open, setOpen };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
